import { Col, Form, Row } from "react-bootstrap";
import Select, { createFilter } from "react-select";
import Creatable from "react-select/creatable";

import { CORVUS_ORGANIZATION_ID } from "../../api/api";
import DatePicker from "../DatePicker";

export function DateFilter({ className, filters, setFilters }) {
    const handleDateChange = ([startSource, endSource]) => {
        if (!startSource) {
            setFilters((prev) => ({
                ...prev,
                startDate: undefined,
                endDate: undefined,
            }));
            return;
        }

        const startDate = new Date(startSource);
        startDate.setHours(0, 0, 0, 0);

        const endDate = new Date(endSource ?? startSource);
        endDate.setHours(23, 59, 59, 999);

        setFilters((prev) => ({
            ...prev,
            startDate,
            endDate,
        }));
    };

    return (
        <DatePicker
            className={className}
            mode="range"
            placeholder="Filter by date"
            value={[filters.startDate, filters.endDate]}
            onChange={handleDateChange}
        />
    );
}

export default function MissionFilters({
    className,
    filters,
    setFilters,
    droneOptions,
    organizationOptions,
    showCorvusToggle = true,
    showDeletedToggle = true,
    showOrganizationFilter = false,
}) {
    const handleDroneChange = (options) => {
        setFilters({
            ...filters,
            droneIds: options.map((d) => d.value),
        });
    };

    const handleCreateDrone = (inputValue) => {
        const newOption = {
            label: inputValue.toUpperCase(),
            value: inputValue.toUpperCase(),
        };
        handleDroneChange([
            ...filters.droneIds.map((id) => ({ label: id, value: id })),
            newOption,
        ]);
    };

    return (
        <Form className={className} onSubmit={(e) => e.preventDefault()}>
            <Form.Group>
                {showCorvusToggle && (
                    <Form.Check
                        type="switch"
                        id="show-corvus-switch"
                        label={"Show Corvus missions"}
                        checked={filters.showCorvus}
                        onChange={(e) =>
                            setFilters((prev) => ({
                                ...prev,
                                showCorvus: e.target.checked,
                            }))
                        }
                    />
                )}
                {showDeletedToggle && (
                    <Form.Check
                        type="switch"
                        id="show-deleted-switch"
                        label={"Show deleted missions"}
                        checked={filters.showDeleted}
                        onChange={(e) =>
                            setFilters((prev) => ({
                                ...prev,
                                showDeleted: e.target.checked,
                            }))
                        }
                    />
                )}
            </Form.Group>
            <Form.Group>
                <Row className="g-2 align-items-center">
                    {/* Date filter */}
                    <Col xs={12} md={4}>
                        <DateFilter filters={filters} setFilters={setFilters} />
                    </Col>

                    {/* Organization filter */}
                    {showOrganizationFilter && (
                        <Col xs={12} md={4}>
                            <Select
                                isClearable
                                value={
                                    organizationOptions.find(
                                        (o) =>
                                            o.value === filters.organizationId,
                                    ) ?? null
                                }
                                onChange={(option) => {
                                    setFilters((prev) => {
                                        // cleared
                                        if (!option) {
                                            const rest = { ...prev };
                                            delete rest.organizationId;
                                            return rest;
                                        }

                                        // selected
                                        return {
                                            ...prev,
                                            organizationId: option.value,
                                            showCorvus:
                                                option.value ===
                                                CORVUS_ORGANIZATION_ID,
                                        };
                                    });
                                }}
                                options={organizationOptions}
                                placeholder="Filter by organization"
                                filterOption={createFilter({
                                    stringify: (option) => option.label,
                                })} // Only match labels when typing
                                styles={{
                                    menu: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                    }),
                                }}
                            />
                        </Col>
                    )}
                </Row>
            </Form.Group>
            <Form.Group className="mt-2">
                <Creatable
                    isMulti
                    isClearable
                    options={droneOptions.map((droneId) => {
                        return {
                            label: droneId.toUpperCase(),
                            value: droneId.toUpperCase(),
                        };
                    })}
                    value={filters.droneIds.map((id) => ({
                        label: id,
                        value: id,
                    }))}
                    onChange={handleDroneChange}
                    onCreateOption={handleCreateDrone}
                    placeholder={"Filter by drone"}
                    formatCreateLabel={(inputValue) =>
                        `${inputValue.toUpperCase()}`
                    }
                    noOptionsMessage={() => "Type a drone name to filter on it"}
                    styles={{
                        menu: (base) => ({
                            ...base,
                            zIndex: 9999,
                        }),
                    }}
                />
            </Form.Group>
        </Form>
    );
}
